"use client"

import { useState, useEffect, useRef, useCallback } from "react"

export type CardPosition = "auto" | "above" | "below" | "left" | "right" | "center"

interface TourCardProps {
  title: string
  description: string
  currentStep: number // 1-based for display
  totalSteps: number
  onNext: () => void
  onPrevious: () => void
  onClose: () => void
  targetIds?: string[]
  targetRect?: DOMRect | null // Optional direct rect override
  isMobile: boolean
  cardPosition?: CardPosition // Preferred card position relative to target
}

export function TourCard({
  title,
  description,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onClose,
  targetIds,
  targetRect: targetRectProp,
  isMobile,
  cardPosition = "auto",
}: TourCardProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(targetRectProp || null)
  const [displayedText, setDisplayedText] = useState("")
  const [isTypingComplete, setIsTypingComplete] = useState(false)
  const retryCountRef = useRef(0)
  const cardRef = useRef<HTMLDivElement>(null)

  const isFirst = currentStep === 1
  const isLast = currentStep === totalSteps

  // Use prop rect if provided, otherwise find by ID
  useEffect(() => {
    if (targetRectProp) {
      setTargetRect(targetRectProp)
      return
    }

    const findTarget = () => {
      if (!targetIds || targetIds.length === 0) {
        setTargetRect(null)
        return
      }

      for (const id of targetIds) {
        const element = document.getElementById(id)
        if (element) {
          const rect = element.getBoundingClientRect()
          // Verify element is visible
          if (rect.width > 0 && rect.height > 0) {
            setTargetRect(rect)
            retryCountRef.current = 0
            return
          }
        }
      }

      // Retry up to 10 times for lazy-loaded elements
      if (retryCountRef.current < 10) {
        retryCountRef.current++
        setTimeout(findTarget, 100)
      } else {
        setTargetRect(null)
      }
    }

    findTarget()
    retryCountRef.current = 0

    // Update on resize/scroll
    const handleUpdate = () => findTarget()
    window.addEventListener("resize", handleUpdate)
    window.addEventListener("scroll", handleUpdate, true)

    return () => {
      window.removeEventListener("resize", handleUpdate)
      window.removeEventListener("scroll", handleUpdate, true)
    }
  }, [targetIds, targetRectProp])

  // Typewriter effect
  useEffect(() => {
    setDisplayedText("")
    setIsTypingComplete(false)

    let index = 0
    const interval = setInterval(() => {
      if (index < description.length) {
        setDisplayedText(description.slice(0, index + 1))
        index++
      } else {
        setIsTypingComplete(true)
        clearInterval(interval)
      }
    }, 20)

    return () => clearInterval(interval)
  }, [description])

  // Skip typewriter on click
  const handleCardClick = useCallback(() => {
    if (!isTypingComplete) {
      setDisplayedText(description)
      setIsTypingComplete(true)
    }
  }, [isTypingComplete, description])

  // Calculate card position
  const getCardStyle = (): React.CSSProperties => {
    if (isMobile) {
      // Mobile: full width at top or bottom based on cardPosition or target location
      let placeAtBottom = false

      if (cardPosition === "below") {
        placeAtBottom = true
      } else if (cardPosition === "above") {
        placeAtBottom = false
      } else {
        // Auto: place opposite to target
        placeAtBottom = targetRect ? targetRect.top < window.innerHeight / 2 : false
      }

      return {
        position: "fixed",
        left: 0,
        right: 0,
        ...(placeAtBottom ? { bottom: 0 } : { top: 0 }),
        margin: 0,
        borderRadius: 0,
        zIndex: 60,
      }
    }

    // Desktop positioning
    if (targetRect) {
      const padding = 20
      const cardWidth = 320
      const cardHeight = 180 // Approximate

      let left: number
      let top: number

      if (cardPosition === "below") {
        // Position below target
        left = targetRect.left + targetRect.width / 2 - cardWidth / 2
        top = targetRect.bottom + padding
        // Keep within horizontal bounds
        left = Math.max(padding, Math.min(window.innerWidth - cardWidth - padding, left))
      } else if (cardPosition === "above") {
        // Position above target
        left = targetRect.left + targetRect.width / 2 - cardWidth / 2
        top = targetRect.top - cardHeight - padding
        // Keep within horizontal bounds
        left = Math.max(padding, Math.min(window.innerWidth - cardWidth - padding, left))
      } else if (cardPosition === "left") {
        // Position to the left of target
        left = targetRect.left - cardWidth - padding
        top = targetRect.top + targetRect.height / 2 - cardHeight / 2
      } else if (cardPosition === "right") {
        // Position to the right of target
        left = targetRect.right + padding
        top = targetRect.top + targetRect.height / 2 - cardHeight / 2
      } else {
        // Auto: try right first, then left if needed
        left = targetRect.right + padding
        top = targetRect.top + targetRect.height / 2 - cardHeight / 2

        // If too far right, place to the left
        if (left + cardWidth > window.innerWidth - padding) {
          left = targetRect.left - cardWidth - padding
        }
      }

      // Keep within vertical bounds
      top = Math.max(padding, Math.min(window.innerHeight - cardHeight - padding, top))

      return {
        position: "fixed",
        left: `${left}px`,
        top: `${top}px`,
        width: `${cardWidth}px`,
        zIndex: 60,
      }
    }

    // Default: center of screen
    return {
      position: "fixed",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      width: "320px",
      zIndex: 60,
    }
  }

  return (
    <>
      {/* Dark overlay with cutout */}
      <div className="fixed inset-0 z-55 pointer-events-none">
        <svg className="w-full h-full">
          <defs>
            <mask id="tour-cutout-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 4}
                  y={targetRect.top - 4}
                  width={targetRect.width + 8}
                  height={targetRect.height + 8}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.7)"
            mask="url(#tour-cutout-mask)"
          />
        </svg>
      </div>

      {/* Clickable backdrop to close */}
      <div className="fixed inset-0 z-55 cursor-pointer" onClick={onClose} />

      {/* Yellow highlight border around target */}
      {targetRect && (
        <div
          className="fixed z-56 border-2 border-yellow-400 rounded-lg pointer-events-none"
          style={{
            left: targetRect.left - 4,
            top: targetRect.top - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: "0 0 20px rgba(250, 204, 21, 0.5), inset 0 0 20px rgba(250, 204, 21, 0.1)",
          }}
        />
      )}

      {/* Tour card */}
      <div
        ref={cardRef}
        className={`bg-white text-black shadow-2xl ${isMobile ? "" : "rounded-xl"}`}
        style={getCardStyle()}
        onClick={handleCardClick}
      >
        <div className={`p-4 ${isMobile ? "pb-6 pt-4" : ""}`}>
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-black/10 transition-colors"
            aria-label="Close tour"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Title */}
          <h3 className="text-lg font-bold mb-2 pr-8">{title}</h3>

          {/* Description with typewriter effect */}
          <p className={`text-sm text-gray-700 mb-4 min-h-[3em] ${isMobile ? "text-base" : ""}`}>
            {displayedText}
            {!isTypingComplete && <span className="animate-pulse">|</span>}
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Step {currentStep} of {totalSteps}
            </span>
            <div className="flex gap-2">
              {!isFirst && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onPrevious()
                  }}
                  className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-black rounded-lg text-sm font-medium transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onNext()
                }}
                className="px-4 py-1.5 bg-black hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {isLast ? "Finish" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
